import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ContactInformationFields } from "src/brand/EC/components/create-client/ContactInformationFields";
import {
  CreateClientEIForm,
  CreateClientEIFormBase,
} from "src/brand/EC/components/create-client/CreateClientEIForm";
import { useCreateClientEC } from "src/brand/EC/hooks/useCreateClientEC";
import IconEntreprise from "src/core/assets/images/icons/entreprise.svg?react";
import IconHuman from "src/core/assets/images/icons/human.svg?react";
import IconLightning from "src/core/assets/images/icons/lightning.svg?react";
import IconMouseKeyboard from "src/core/assets/images/icons/mouseKeyboard.svg?react";
import { DuplicateFoldersList } from "src/core/components/create-client/DuplicateFoldersList";
import { FollowFolderInfo } from "src/core/components/create-client/FollowFolderInfo";
import { FormInput } from "src/core/components/custom-ui/form-input";
import { Button } from "src/core/components/ui/button";
import { DialogFooter } from "src/core/components/ui/dialog";
import { Form } from "src/core/components/ui/form";
import { cn } from "src/core/lib/utils";

export const CreateClientEC = ({ setOpen }: { setOpen: (open: boolean) => void }) => {
  const { t } = useTranslation();
  const {
    type,
    setType,
    mode,
    setMode,
    showContactInfo,
    setShowContactInfo,
    form,
    showSirenForm,
    showRaisonSocialeForm,
    showEIForm,
    showForm,
    duplicateFolders,
    isLoading,
    onSubmit,
    shouldConfirmDuplicate,
    setHasConfirmedDuplicate,
    hasExactDuplicate,
  } = useCreateClientEC();
  return (
    <div className="modal-create-client-description flex flex-col gap-y-10 whitespace-pre-line">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="contact-wrapper">
            <FollowFolderInfo form={form} />
            <ContactInformationFields
              form={form}
              setShowContactInfo={setShowContactInfo}
              showContactInfo={showContactInfo}
            />
          </div>

          <div className="flex flex-col gap-y-10 ml-20 mb-20">
            <div className="flex flex-row items-center gap-x-10">
              <div className="step-chip">1</div>
              <div className="">{t("createClient.modal.stepType")}</div>
            </div>
            <div className="flex flex-row items-center gap-x-40">
              <div
                className={cn("folder-card", type === "corporation" && "active")}
                role="button"
                onClick={() => setType("corporation")}
              >
                <span className="text-2xl">
                  <IconEntreprise className="h-30 w-20" />
                </span>
                <div className="text-center">
                  <div>{t("createClient.modal.createCorporation")}</div>
                  <div className="text-xs font-light">
                    {t("createClient.modal.createCorporationSubtitle")}
                  </div>
                </div>
              </div>
              <div
                className={cn("folder-card", type === "individual" && "active")}
                role="button"
                onClick={() => setType("individual")}
              >
                <span className="text-2xl">
                  <IconHuman />
                </span>
                <div className="text-center">
                  <div>{t("createClient.modal.createEI")}</div>
                  <div className="text-xs font-light">
                    {t("createClient.modal.createEISubtitle")}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`${showForm ? "contact-wrapper" : "ml-20"} flex flex-col gap-y-10`}>
            <div className="flex flex-row items-center gap-x-10">
              <div className="step-chip">2</div>
              <div className="">{t("createClient.modal.stepMode")}</div>
            </div>
            <div className="flex flex-row items-center gap-x-40">
              <div
                className={cn("folder-card", mode === "siren" && "active")}
                role="button"
                onClick={() => {
                  if (type !== null) {
                    setMode("siren");
                  }
                }}
              >
                <span className="text-2xl">
                  <IconLightning />
                </span>
                <div className="text-center">
                  <div>{t("createClient.modal.createWithSiren")}</div>
                  <div className="text-xs font-light">
                    {t("createClient.modal.createWithSirenSubtitle")}
                  </div>
                </div>
              </div>
              <div
                className={cn("folder-card", mode === "raisonSociale" && "active")}
                role="button"
                onClick={() => {
                  if (type !== null) {
                    setMode("raisonSociale");
                  }
                }}
              >
                <span className="text-2xl">
                  <IconMouseKeyboard />
                </span>
                <div className="text-center">
                  <div>{t("createClient.modal.createWithRaisonSociale")}</div>
                  <div className="text-xs font-light">
                    {t("createClient.modal.createWithRaisonSocialeECSubtitle")}
                  </div>
                </div>
              </div>
            </div>
            {showForm && (
              <div className="flex flex-col gap-y-10">
                {showSirenForm && (
                  <div className="flex flex-row gap-x-20 items-center">
                    <FormInput
                      className="w-[360px]"
                      control={form.control}
                      name="siren"
                      label={t("form.createClient.siren")}
                      onPaste={(event) => {
                        event.preventDefault();
                        let siren = event.clipboardData.getData("text");
                        siren = siren.replace(/\D+/g, "").slice(0, 9);
                        form.setValue("siren", siren);
                      }}
                      onChange={(event) => {
                        const siren = event.target.value.replace(/\D+/g, "").slice(0, 9);
                        form.setValue("siren", siren);
                      }}
                      required
                    />

                    <DuplicateFoldersList duplicateFolders={duplicateFolders} />
                  </div>
                )}
                {showRaisonSocialeForm && (
                  <div className="flex flex-row gap-x-20 items-center">
                    <FormInput
                      control={form.control}
                      name="raisonSociale"
                      className="w-[360px]"
                      label={t("form.createClient.raisonSociale")}
                      required
                    />
                    <DuplicateFoldersList duplicateFolders={duplicateFolders} />
                  </div>
                )}
                {showEIForm && (
                  <div className="flex flex-col gap-y-10">
                    <CreateClientEIForm
                      form={form as unknown as UseFormReturn<CreateClientEIFormBase>}
                    />
                    <DuplicateFoldersList
                      duplicateFolders={duplicateFolders}
                      wrapperClassName="flex flex-col"
                      listClassName="grid grid-cols-2 gap-x-20 mt-5"
                      withTitle={!hasExactDuplicate}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="modal-create-client-footer mt-20">
            <div className="flex flex-row gap-x-20">
              <Button
                variant="link"
                onClick={(event) => {
                  event.preventDefault();
                  setOpen(false);
                }}
              >
                {t("global.form.cancel")}
              </Button>
              <Button
                type="submit"
                variant={
                  duplicateFolders.length > 0 && mode === "raisonSociale" && !hasExactDuplicate
                    ? "tertiary"
                    : "default"
                }
                loading={isLoading}
                onClick={() => {
                  if (shouldConfirmDuplicate && mode === "raisonSociale") {
                    setHasConfirmedDuplicate(true);
                  }
                }}
                data-testid="creation-button-raison-sociale"
              >
                {t(
                  duplicateFolders.length > 0 && mode === "raisonSociale" && !hasExactDuplicate
                    ? "form.createClient.continueCreation"
                    : "form.createClient.submit"
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
};
